import React from "react";
import { Form, InputGroup } from "react-bootstrap";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import { useGetMatchingReportingTemplatesQuery } from "../store/slices/reportingSlice";
import {
  getReportingTemplateFieldByKey,
  getReportingTemplateFieldMappingUpdates,
} from "./utils/solutionImportHelpers";
// import JsonCollapse from "../Builder/Components/JsonCollapse";


export default function SolutionImportCheckReportingTemplate({
  importedJSON,
  setImportedJSON,
  templateKey: key,
  value,
  manualFields,
  setManualFields,
  setAutomaticallySelectedFields = () => {},
}: {
  importedJSON: any;
  setImportedJSON: React.Dispatch<React.SetStateAction<any>>;
  templateKey: string;
  value: any;
  manualFields: string[];
  setManualFields: React.Dispatch<React.SetStateAction<string[]>>;
  setAutomaticallySelectedFields?: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const templateData = importedJSON.Item?.ReportingTemplates?.find(
    (f: any) => f.uuid === key
  );

  // Fetch matching reporting templates
  const { data: matchingTemplatesData } = useGetMatchingReportingTemplatesQuery(
    {
      label: templateData?.label || "",
      fields: templateData?.fields || [],
    },
    {
      skip: !templateData,
    }
  );

  const matchingTemplates = matchingTemplatesData?.data || [];

  const applyTemplateFieldMappings = (updated: any, selectedTemplate: any) => {
    Object.entries(
      getReportingTemplateFieldMappingUpdates(
        templateData,
        selectedTemplate,
        updated.Bag.External.ReportingTemplateField || {}
      )
    ).forEach(([fieldKey, fieldValue]) => {
      updated.Bag.External.ReportingTemplateField[fieldKey] = fieldValue;
    });
  };

  return (
    <>
      <InputGroup>
        <InputGroupText className="col-3">
          {templateData?.label}
        </InputGroupText>
        {!value && !manualFields.includes(key) ? (
          <InputGroupText className="text-muted col" style={{ height: 40 }}>
            This template will be created automatically upon deployment. Or you
            can{" "}
            <button
              className="btn btn-link p-0 align-baseline ms-1"
              onClick={() => {
                setManualFields((prev) => [...prev, key]);
              }}
            >
              manage manually
            </button>
            .
          </InputGroupText>
        ) : manualFields.includes(key) ? (
          <div className="col ms-n1">
            <div className="mb-2">
              <Form.Check
                inline
                type="radio"
                id={`${key}-template-select-existing`}
                label="Select existing template"
                name={`${key}-template-choice`}
                checked={
                  value !== null &&
                  value !== undefined &&
                  !value.startsWith("NEW:")
                }
                onChange={() => {
                  setImportedJSON((prev: any) => {
                    const updated = { ...prev };
                    updated.Bag.External.ReportingTemplate[key] = "";
                    return updated;
                  });
                }}
              />
              <Form.Check
                inline
                type="radio"
                id={`${key}-template-create-new`}
                label="Create new template"
                name={`${key}-template-choice`}
                checked={
                  value === null ||
                  value === undefined ||
                  value.startsWith("NEW:")
                }
                onChange={() => {
                  setImportedJSON((prev: any) => {
                    const updated = { ...prev };
                    updated.Bag.External.ReportingTemplate[key] = "NEW:";
                    return updated;
                  });
                }}
              />
            </div>
            {value === "" || (value && !value.startsWith("NEW:")) ? (
              matchingTemplates && (
                <Input
                  placeholder="Select an existing template"
                  inputType="select"
                  value={[value]}
                  onChange={([v]: any) => {
                    setImportedJSON((prev: any) => {
                      const updated = { ...prev };
                      updated.Bag.External.ReportingTemplate[key] = v;
                      const selectedTemplate = matchingTemplates.find(
                        (template: any) => template.uuid === v
                      );
                      applyTemplateFieldMappings(updated, selectedTemplate);
                      return updated;
                    });
                  }}
                  options={matchingTemplates?.map((cf: any) => ({
                    value: cf.uuid,
                    label: cf.label,
                  }))}
                />
              )
            ) : (
              <Form.Control
                type="text"
                placeholder="Enter a name for the new template"
                value={value?.startsWith("NEW:") ? value.substring(4) : ""}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const trimmedValue = newValue.trim();
                  setImportedJSON((prev: any) => {
                    const updated = { ...prev };
                    updated.Bag.External.ReportingTemplate[key] = trimmedValue
                      ? `NEW:${newValue}`
                      : "NEW:";
                    return updated;
                  });
                }}
              />
            )}
          </div>
        ) : (
          <div className="col ms-n1">
            {matchingTemplates && (
              <InputGroupText className="text-muted w-100" style={{ height: 40 }}>
                {matchingTemplates.find((template: any) => template.uuid === value)
                  ?.label || value}
              </InputGroupText>
            )}
          </div>
        )}
        <div className="col-auto d-flex justify-content-end align-items-center">
          <Form.Check
            id={`reporting-template-manual-${key}`}
            type="switch"
            label="Manage manually"
            checked={manualFields.includes(key)}
            onChange={(e) => {
              if (e.target.checked) {
                setManualFields((prev) => [...prev, key]);
                setAutomaticallySelectedFields((prev) =>
                  prev.filter((fieldKey) => fieldKey !== key)
                );
              } else {
                setManualFields((prev) => prev.filter((f) => f !== key));

                const oldField = importedJSON.Item?.ReportingTemplates?.find(
                  (f: any) => f.uuid === key
                );
                const matchingTemplate =
                  matchingTemplates.find(
                    (template: any) => template.label === oldField?.label
                  ) || matchingTemplates[0];
                setImportedJSON((prev: any) => {
                  const updated = { ...prev };
                  updated.Bag.External.ReportingTemplate[key] =
                    matchingTemplate?.uuid || "";
                  applyTemplateFieldMappings(updated, matchingTemplate);

                  return updated;
                });
                setAutomaticallySelectedFields((prev) => {
                  const withoutField = prev.filter((fieldKey) => fieldKey !== key);
                  return matchingTemplate?.uuid ? [...withoutField, key] : withoutField;
                });
              }
            }}
            className="ms-3"
          />
        </div>
      </InputGroup>
      {/* <div className="overflow-auto">
        <div>
          <JsonCollapse
            defaulPlainText
            label="needed"
            json={templateData}
          />
        </div>
        <div>
          <JsonCollapse
            defaulPlainText
            label="selected"
            json={matchingTemplates?.find((f: any) => f.uuid === value)}
          />
        </div>
      </div> */}
      {manualFields.includes(key) && value && !value.startsWith("NEW:") && (
        <div key={value} className="mb-5 ms-4 border-start ps-3">
          {importedJSON.Bag.External.ReportingTemplateField && (
            <h4 className="mt-3">Template Fields</h4>
          )}
          {/* <JsonCollapse
            defaulPlainText
            json={Object.entries(
              importedJSON.Bag.External.ReportingTemplateField || {}
            ).filter(([key2, value2]: [string, any]) =>
              importedJSON.Item.ReportingTemplates.some(
                (f: any) =>
                  f.uuid === key &&
                  f.fields.find((ff: any) => ff.uuid === key2)
              )
            )}
          /> */}
          {Object.entries(
            importedJSON.Bag.External.ReportingTemplateField || {}
          )
            .filter(([key2, value2]: [string, any]) =>
              !!getReportingTemplateFieldByKey(templateData, key2)
            )
            .map(([key2, value2]: [string, any]) => (
              <div key={key2} className="mb-3">
                <InputGroup>
                  <InputGroupText className="col-3">
                    {getReportingTemplateFieldByKey(templateData, key2)?.label}
                  </InputGroupText>
                  <div className="col ms-n1">
                    {matchingTemplates && (
                      <Input
                        placeholder="Select an existing template field or type a name and select Create"
                        inputType="select"
                        value={[value2]}
                        isValidNewOption={(v) => !!v}
                        onCreateOption={(newValue) => {
                          setImportedJSON((prev: any) => {
                            const updated = { ...prev };
                            updated.Bag.External.ReportingTemplateField[key2] =
                              `NEW:${newValue}`;
                            return updated;
                          });
                        }}
                        onChange={([v]: any) => {
                          setImportedJSON((prev: any) => {
                            const updated = { ...prev };
                            updated.Bag.External.ReportingTemplateField[key2] =
                              v;
                            return updated;
                          });
                        }}
                        options={matchingTemplates
                          ?.find((f: any) => f.uuid === value)
                          ?.fields?.map((cf: any) => ({
                            value: cf.uuid,
                            label: cf.label,
                          }))}
                      />
                    )}
                  </div>
                </InputGroup>
                {/* <div className="overflow-auto">
                  <div>
                    <JsonCollapse
                      defaulPlainText
                      label="needed"
                      json={importedJSON.Item.ReportingTemplates.find(
                        (f: any) => f.uuid === key
                      )?.fields.find((f: any) => f.uuid === key2)}
                    />
                  </div>
                  <div>
                    <JsonCollapse
                      defaulPlainText
                      label="selected"
                      json={matchingTemplates
                        ?.find((f: any) => f.uuid === value)
                        ?.fields.find((f: any) => f.uuid === value2)}
                    />
                  </div>
                </div> */}
              </div>
            ))}
        </div>
      )}
    </>
  );
}
